import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { fadeUp, staggerContainer } from '@/animations/variants';
import Button from './Button';
import ChatbotModal from '@/components/chatbot/ChatbotModal';
import { useState } from 'react';
import { FaFacebookF, FaInstagram } from 'react-icons/fa6';

const footerLinks = {
  company: [
    { name: 'About', href: '/about' },
    { name: 'Services', href: '/services' },
    { name: 'Industries', href: '/industries' },
    { name: 'Technology', href: '/technology' },
  ],
  resources: [
    { name: 'Insights', href: '/insights' },
    { name: 'Case Studies', href: '/insights' },
    { name: 'Blog', href: '/insights' },
    { name: 'Documentation', href: '/insights' },
  ],
  support: [
    { name: 'Contact', href: '/contact' },
    { name: 'Help Center', href: '/contact' },
    { name: 'Privacy Policy', href: '/privacy-policy', external: true },
    { name: 'Terms of Service', href: '/terms-of-service', external: true },
    { name: 'Data Deletion', href: '/data-deletion', external: true },
    { name: 'Refund & Cancellation', href: '/refund-policy', external: true },
    { name: 'Pricing', href: '/pricing', external: true },
  ], 
  contact: [
    { name: 'Email: connect@ennobridge.com', href: 'mailto:connect@ennobridge.com' },
    { name: 'Phone: +91 9270161536', href: 'tel:+919270161536' },
    { name: 'Address: Ennobridge Tech, Hinjewadi IT Park - Phase 3, Pune', href: '#' },
  ],
};

export default function Footer() {
  const [isChatbotOpen, setIsChatbotOpen] = useState(false);
  
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  return (
    <motion.footer 
      className="bg-surface border-t border-border"
      variants={staggerContainer}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 lg:gap-12">
          {/* Company Links */}
          <motion.div variants={fadeUp} className="flex-1">
            <h3 className="text-sm font-semibold text-textPrimary uppercase tracking-wider mb-4">
              Company
            </h3>
            <ul className="space-y-3 sm:space-y-4">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    onClick={scrollToTop}
                    className="text-base text-textSecondary hover:text-primary transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Resources Links */}
          <motion.div variants={fadeUp} className="flex-1">
            <h3 className="text-sm font-semibold text-textPrimary uppercase tracking-wider mb-4">
              Resources
            </h3>
            <ul className="space-y-3 sm:space-y-4">
              {footerLinks.resources.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.href}
                    onClick={scrollToTop}
                    className="text-base text-textSecondary hover:text-primary transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Support Links */}
          <motion.div variants={fadeUp} className="flex-1">
            <h3 className="text-sm font-semibold text-textPrimary uppercase tracking-wider mb-4">
              Support
            </h3>
            <ul className="space-y-3 sm:space-y-4">
              {footerLinks.support.map((link) => (
                <li key={link.name}>
                  {link.external ? (
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-base text-textSecondary hover:text-primary transition-colors"
                    >
                      {link.name}
                    </a>
                  ) : (
                    <Link
                      to={link.href}
                      onClick={scrollToTop}
                      className="text-base text-textSecondary hover:text-primary transition-colors"
                    >
                      {link.name}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Contact Links */}
          <motion.div variants={fadeUp} className="flex-1">
            <h3 className="text-sm font-semibold text-textPrimary uppercase tracking-wider mb-4">
              Contact
            </h3>
            <ul className="space-y-3 sm:space-y-4">
              {footerLinks.contact.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.href}
                    onClick={link.href.startsWith('/') ? scrollToTop : undefined}
                    className="text-base text-textSecondary hover:text-primary transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
            <div className="mt-6">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setIsChatbotOpen(true)}
              >
                Start a Conversation
              </Button>
            </div>
          </motion.div>
        </div>

        {/* Bottom Section */}
        <motion.div 
          className="mt-12 pt-8 border-t border-border flex flex-col sm:flex-row justify-between items-center"
          variants={fadeUp}
        >
          <p className="text-textSecondary text-sm">
            Created by Ennobridge!
            </p>
          <div className="mt-4 sm:mt-0 flex items-center gap-3">
            <a 
              href="https://www.facebook.com/ennobridge/"
              className="inline-flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full text-white transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              style={{ background: 'linear-gradient(180deg, #13a5ed 0%, #0866df 100%)' }}
              aria-label="Facebook"
              title="Facebook"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaFacebookF className="h-9 w-9 translate-y-1" aria-hidden="true" focusable="false" />
            </a>
            <a 
              href="https://www.instagram.com/ennobridgetech"
              className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-white shadow-sm transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              style={{ background: 'radial-gradient(circle at 30% 105%, #feda75 0%, #fa7e1e 25%, #d62976 52%, #962fbf 75%, #4f5bd5 100%)' }}
              aria-label="Instagram"
              title="Instagram"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaInstagram className="h-7 w-7" aria-hidden="true" focusable="false" />
            </a>
          </div>
        </motion.div>
      </div>
      
      {/* Chatbot Modal */}
      <ChatbotModal 
        isOpen={isChatbotOpen} 
        onClose={() => setIsChatbotOpen(false)} 
      />
    </motion.footer>
  );
}